import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiShield,FiCheckCircle,FiAlertTriangle,FiHeart}=FiIcons;

const RiskReversalSection=()=> {
  return (
    <section className="py-20 px-4 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-green-900/10 to-transparent"></div>
      
      {/* Background Effects */}
      <div className="absolute top-20 right-10 w-32 h-32 bg-red-500/10 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-20 left-10 w-40 h-40 bg-orange-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto">
        <motion.div
          initial={{y: 30,opacity: 0}}
          whileInView={{y: 0,opacity: 1}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              RISK REVERSAL
            </span>
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold text-white">
            "เรียนไม่คุ้ม... <span className="text-green-400">คืนเงิน</span>"
          </h3>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Guarantee Section */}
          <motion.div
            initial={{y: 50,opacity: 0}}
            whileInView={{y: 0,opacity: 1}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="bg-slate-800/50 backdrop-blur-sm border border-green-500/30 rounded-2xl p-8 mb-12"
          >
            <div className="text-center mb-8">
              <SafeIcon icon={FiShield} className="text-6xl text-green-400 mx-auto mb-4" />
              <h4 className="text-2xl md:text-3xl font-bold text-white mb-4">
                การันตีความมั่นใจ 100%
              </h4>
            </div>
            <div className="space-y-6 text-gray-300">
              <p className="text-lg leading-relaxed">
                ผมมั่นใจว่า แค่คุณเอาวิชานี้ไปสแกนคนที่จะเข้ามาในชีวิต คุณได้<span className="text-green-400 font-semibold">"แค่คนเดียว"</span>
              </p>
              <p className="text-lg leading-relaxed">
                มันก็คุ้มค่าเรียนไปตลอดชีวิตแล้ว
              </p>
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                <div className="flex items-start">
                  <SafeIcon icon={FiCheckCircle} className="text-2xl text-green-400 mr-4 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-lg font-semibold text-green-300 mb-2">
                      แต่ถ้าคุณเรียนแล้วรู้สึกว่าใช้ไม่ได้จริง...
                    </p>
                    <p className="text-xl font-bold text-white">
                      ผมยินดีคืนเงินให้ 100%
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      (หักค่าอาหาร/เอกสารตามจริง)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Redesigned Pain Visualization */}
          <motion.div
            initial={{y: 50,opacity: 0}}
            whileInView={{y: 0,opacity: 1}}
            transition={{duration: 0.8,delay: 0.2}}
            viewport={{once: true}}
            className="bg-gradient-to-br from-red-500/20 via-orange-500/20 to-pink-500/20 backdrop-blur-sm border-2 border-red-500/30 rounded-3xl p-8 mb-12 relative overflow-hidden"
          >
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-pink-500/10 animate-gradient"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                  <SafeIcon icon={FiAlertTriangle} className="text-4xl text-red-400 mr-3 animate-pulse" />
                  <h4 className="text-2xl md:text-3xl font-bold text-white">
                    ความเสี่ยงเดียวของคุณ...
                  </h4>
                </div>
                <h5 className="text-xl md:text-2xl font-bold text-orange-400 mb-6">
                  คือการปล่อยให้คนไม่จริงใจ เดินเข้ามาทำร้ายคุณซ้ำๆ
                </h5>
              </div>
              
              <div className="space-y-6 text-gray-300 mb-8">
                <div className="bg-slate-800/40 border border-red-500/20 rounded-2xl p-6">
                  <h6 className="text-lg font-semibold text-red-300 mb-4">🚨 อย่ารอให้เจ็บตัวก่อนค่อยมาเรียนรู้</h6>
                  <p className="text-lg font-medium text-white mb-4">เพราะบทเรียนราคาแพงที่สุด คือ "การไม่รู้"</p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start bg-slate-700/30 rounded-lg p-3">
                      <span className="text-red-400 text-2xl mr-3">💔</span>
                      <p>คนใกล้ตัวที่เราไว้ใจมากที่สุด... แต่กลับแอบแทงเราจากข้างหลัง</p>
                    </div>
                    
                    <div className="flex items-start bg-slate-700/30 rounded-lg p-3">
                      <span className="text-red-400 text-2xl mr-3">💰</span>
                      <p>โอกาสทางธุรกิจที่ดูสวยงาม... แต่จบลงด้วยการโดนหักหลังจนเกือบล้มละลาย</p>
                    </div>
                    
                    <div className="flex items-start bg-slate-700/30 rounded-lg p-3">
                      <span className="text-red-400 text-2xl mr-3">⚡</span>
                      <p>ความสัมพันธ์ที่เราทุ่มเททุกอย่าง... แต่สุดท้ายเขากลับเลือกคนอื่น</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500/30 rounded-2xl p-6">
                  <div className="flex items-start">
                    <span className="text-yellow-400 text-3xl mr-3">💡</span>
                    <div>
                      <p className="text-lg font-semibold text-yellow-300 mb-2">วันนั้นคุณคงยอมจ่ายเป็นแสนเป็นล้าน...</p>
                      <p className="text-lg text-white">เพื่อแลกกับการ <span className="text-green-400 font-bold">"รู้ทัน"</span> ตั้งแต่วันแรก</p>
                      <p className="text-xl text-green-300 font-bold mt-4">แต่วันนี้คุณแลกสิ่งนั้นได้ในราคาหลักพัน!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            initial={{scale: 0.9,opacity: 0}}
            whileInView={{scale: 1,opacity: 1}}
            transition={{duration: 0.8,delay: 0.4}}
            viewport={{once: true}}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-sm border border-green-500/30 rounded-2xl p-8">
              <h4 className="text-2xl md:text-3xl font-bold text-white mb-6">
                ป้องกันดีกว่าแก้ไข... เรียนรู้ดีกว่าเสียใจ
              </h4>
              <button className="group relative px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-xl rounded-full shadow-2xl hover:shadow-green-500/25 transition-all duration-300 transform hover:scale-105 border border-green-400/50 mb-4">
                <span className="flex items-center space-x-2">
                  <SafeIcon icon={FiHeart} className="text-2xl" />
                  <span>เรียนเลย! ปกป้องความสัมพันธ์ของคุณ</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              </button>
              <p className="text-green-300 font-semibold">
                การันตีคืนเงิน 100% หากไม่พอใจ
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default RiskReversalSection;